import { A } from "@solidjs/router";
import { type Component, createEffect, For } from "solid-js";
import { useI18n } from "@/api/i18n";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import { usePostHog } from "@/hooks/usePostHog";

const UPGRADE_HREF = "/profile/company/upgrade";

/** Which locked capability the user just bumped into. */
export type AnonymizationUpsellFeature = "anonymous_cv" | "candidate_report";

export type AnonymizationUpsellModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Decides the copy — the two features are sold on different promises. */
  feature: AnonymizationUpsellFeature;
  /** Entry point, attached to tracking. */
  source: string;
};

/**
 * Upsell shown when a plan without the entitlement tries to generate an
 * anonymous CV or an AI candidate report.
 *
 * Follows the Talent Pool upsell's visual language so the paywall reads as one
 * system, but keeps its own copy: two adjacent padlocks must never open the
 * same generic modal, or the user cannot tell what they are being sold.
 *
 * Deliberately **does not name a plan**. Naming one saves a step but goes stale
 * the first time the pricing table changes, and goes stale silently — the copy
 * stays wrong until someone complains. The benefit is stable; the price list
 * is one click away.
 */
export const AnonymizationUpsellModal: Component<
  AnonymizationUpsellModalProps
> = (props) => {
  const { t } = useI18n();
  const { capture } = usePostHog();

  const key = () => `company.anonymization.upsell.${props.feature}` as const;

  const benefits = () => [
    t(`${key()}.benefits.first` as never) as string,
    t(`${key()}.benefits.second` as never) as string,
    t(`${key()}.benefits.third` as never) as string,
  ];

  // One view event per closed→open transition, not per render.
  createEffect((wasOpen: boolean | undefined) => {
    if (props.open && !wasOpen) {
      capture("view_anonymization_upsell", {
        feature: props.feature,
        source: props.source,
      });
    }
    return props.open;
  });

  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent class="gap-0 overflow-hidden p-0 sm:max-w-md">
        <div class="flex flex-col gap-3 border-b border-border bg-linear-to-br from-primary/5 to-card p-6">
          <div class="flex h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary">
            <i class="ph-fill ph-lock-simple text-2xl" aria-hidden="true" />
          </div>
          <DialogTitle class="text-xl">
            {t(`${key()}.title` as never) as string}
          </DialogTitle>
        </div>

        <div class="flex flex-col gap-5 p-6">
          <DialogDescription class="text-sm leading-relaxed text-muted-foreground">
            {t(`${key()}.value` as never) as string}
          </DialogDescription>

          <ul class="flex flex-col gap-2.5 text-sm text-gray-700">
            <For each={benefits()}>
              {(benefit) => (
                <li class="flex items-center gap-2.5">
                  <i
                    class="ph-fill ph-check-circle shrink-0 text-base text-green-600"
                    aria-hidden="true"
                  />
                  <span>{benefit}</span>
                </li>
              )}
            </For>
          </ul>

          <div class="flex flex-col gap-2">
            <Button
              as={A}
              href={UPGRADE_HREF}
              onClick={() =>
                capture("click_anonymization_upsell_cta", {
                  feature: props.feature,
                  source: props.source,
                })
              }
              size="lg"
              class="w-full"
            >
              {t("company.anonymization.upsell.cta_primary")}
            </Button>
            <Button
              variant="ghost"
              onClick={() => props.onOpenChange(false)}
              class="w-full text-muted-foreground hover:text-foreground"
            >
              {t("company.anonymization.upsell.cta_secondary")}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
